import { IHtpGameConfiguration, IHtpConfiguration, IHtpSlideControlTextures } from '@/components/arcade-game/_components/how-to-play/_domain/modules/htp-config';
import { injectable, inject } from 'inversify';
import { IConfigurationService, ConfigurationKey } from '@/app-modules/config';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import Axios, { AxiosRequestConfig } from 'axios';
import { IAssetConfig, AssetManager } from '@arcade-client/asset-manager';
import localHtpConfig from '@/components/arcade-game/_components/how-to-play/_domain/modules/config.htp.json';
import { IErrorService } from '@/app-modules/error';

interface IHtpConfigurationService {
  getConfigurationForGame(arcadeGameName: string): Promise<IHtpGameConfiguration>;
  getSlideControlAssetNames(): Promise<IHtpSlideControlTextures>;
  loadHtpAssetsForGame(arcadeGameName: string): Promise<void>;
}

@injectable()
class HtpConfigurationService implements IHtpConfigurationService {
  private htpConfiguration: IHtpConfiguration = null;
  private readonly gameAssetsStatusDictionary: { gameName: string, isLoaded: boolean }[] = [];

  constructor(
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) private readonly configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IErrorService) private readonly errorService: IErrorService
  ) {
  }

  public async getConfigurationForGame(arcadeGameName: string): Promise<IHtpGameConfiguration> {
    await this.initialiseHtpConfiguration();
    const htpConfig = this.findConfigurationForGame(arcadeGameName);
    return htpConfig;
  }

  public async getSlideControlAssetNames(): Promise<IHtpSlideControlTextures> {
    await this.initialiseHtpConfiguration();
    return this.htpConfiguration.slideControlTextures;
  }

  public async loadHtpAssetsForGame(arcadeGameName: string): Promise<void> {
    await this.initialiseHtpConfiguration();
    await this.loadSlideControlAssets();

    if (this.areAssetsLoadedForGame(arcadeGameName)) {
      return;
    }

    const assetConfig = this.buildAssetConfigurationForGame(arcadeGameName);
    if (!assetConfig) {
      this.errorService.logError(new Error(`Could not build asset configuration for game ${arcadeGameName}`));
      return;
    }

    await AssetManager.load(assetConfig);
    const isLoaded = true;
    this.setAssetLoadedStatusForGame(arcadeGameName, isLoaded);
  }

  private async loadSlideControlAssets(): Promise<void> {
    const SLIDER_CONTROL_NAME = 'sliderControl';
    if (this.areAssetsLoadedForGame(SLIDER_CONTROL_NAME)) {
      return;
    }

    const assetConfig = this.buildAssetConfigurationForSlideControlAssets();
    await AssetManager.load(assetConfig);
    const isLoaded = true;
    this.setAssetLoadedStatusForGame(SLIDER_CONTROL_NAME, isLoaded);
  }

  private buildAssetConfigurationForSlideControlAssets(): IAssetConfig {
    const slideControlTextures = this.htpConfiguration.slideControlTextures;
    return {
      baseUrl: this.getAssetCdnUrl(),
      assetList: {
        textures: [
          slideControlTextures.enabled,
          slideControlTextures.disabled
        ]
      },
      skin: '',
      version: ''
    };
  }

  private buildAssetConfigurationForGame(arcadeGameName: string): IAssetConfig {
    const htpConfig = this.findConfigurationForGame(arcadeGameName);
    if (htpConfig) {
      return {
        baseUrl: this.getAssetCdnUrl(),
        assetList: JSON.parse(JSON.stringify(htpConfig.assetList)),
        skin: '',
        version: ''
      };
    }
  }

  private getAssetCdnUrl(): string {
    return this.configurationService.getConfigurationItem(ConfigurationKey.ARCADE_HTP_CDN_URL);
  }

  private findConfigurationForGame(arcadeGameName: string): IHtpGameConfiguration {
    let htpConfig = this.htpConfiguration.games.find((game) => game.name.toLowerCase() === arcadeGameName.toLowerCase());
    if (!htpConfig) {
      htpConfig = this.htpConfiguration.games.find((game) => {
        const skins = game.assetList.skins;
        if (skins.length === 1) {
          return skins[0].toLowerCase() === arcadeGameName.toLowerCase();
        }
        return false;
      });
    }
    return this.updateHtpConfigurationReferences(htpConfig);
  }

  private updateHtpConfigurationReferences(htpConfig: IHtpGameConfiguration): IHtpGameConfiguration {
    if (htpConfig) {
      const gameName = htpConfig.name;
      const updatedHtpConfig: IHtpGameConfiguration = JSON.parse(JSON.stringify(htpConfig));
      updatedHtpConfig.assetList.textures.forEach((o) => o.name = `${gameName}-${o.name}`);
      updatedHtpConfig.assetList.spriteSheets.forEach((o) => o.name = `${gameName}-${o.name}`);
      updatedHtpConfig.assetList.animations.forEach((o) => {
        o.name = `${gameName}-anim-${o.name}`;
        o.texture = `${gameName}-${o.texture}`;
        o.atlas = `${gameName}-${o.atlas}`;
      });
      return updatedHtpConfig;
    }
    return null;
  }

  private areAssetsLoadedForGame(arcadeGameName: string): boolean {
    return this.gameAssetsStatusDictionary.some((status) => status.gameName.toLowerCase() === arcadeGameName.toLowerCase() && status.isLoaded);
  }

  private setAssetLoadedStatusForGame(arcadeGameName: string, isLoaded: boolean) {
    let assetStatus = this.gameAssetsStatusDictionary.find((status) => status.gameName.toLowerCase() === arcadeGameName.toLowerCase());
    if (assetStatus) {
      assetStatus.isLoaded = isLoaded;
    }
    else {
      assetStatus = {
        gameName: arcadeGameName,
        isLoaded
      };
      this.gameAssetsStatusDictionary.push(assetStatus);
    }
  }

  private async initialiseHtpConfiguration(): Promise<void> {
    if (!this.htpConfiguration) {
      this.htpConfiguration = await this.loadHtpConfiguration();
    }
  }

  private async loadHtpConfiguration(): Promise<IHtpConfiguration> {
    const configEnvironment = this.configurationService.getConfigurationItem(ConfigurationKey.CONFIG_ENV);
    const isLocalEnvironment = (configEnvironment === 'local');
    const htpConfig = (!isLocalEnvironment) ? await this.getHtpConfigurationFromServer() : null;
    return htpConfig || localHtpConfig;
  }

  private async getHtpConfigurationFromServer(): Promise<IHtpConfiguration> {
    try {
      const requestConfig: AxiosRequestConfig = {
        headers: {
          'content-type': 'application/json'
        }
      };
      const requestUrl = this.configurationService.getConfigurationItem(ConfigurationKey.ARCADE_HTP_CONFIG_URL);
      const response = await Axios.get(requestUrl, requestConfig);
      if (response && response.status === 200 && response.data) {
        return response.data as IHtpConfiguration;
      }

      this.errorService.logError(new Error('Could not retrieve arcade game HTP configuration.'));
      return null;
    }
    catch (err) {
      this.errorService.logError(err);
    }
    return null;
  }
}

export { IHtpConfigurationService, HtpConfigurationService };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/arcade-game/_components/how-to-play/_domain/modules/htp-config-service.ts